// src/components/ProjectCard.js
import React from 'react';
import styled from 'styled-components';
import { colors, fonts, fontSizes, fontWeights, radii, shadows, space, duration, ease } from '../styles/tokens';
import { StackTag, StackTagList } from './StackTag';
import { Icon } from './Icon';

const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${colors.bgElev};
  border: 1px solid ${colors.border};
  border-radius: ${radii.xl};
  padding: ${space[3]};
  cursor: pointer;
  box-shadow: ${shadows.card};
  transition: border-color ${duration.base} ${ease.soft},
              box-shadow ${duration.base} ${ease.soft},
              transform ${duration.base} ${ease.soft};

  &:hover {
    border-color: ${colors.borderHi};
    box-shadow: ${shadows.cardHover}, ${shadows.glowAccent};
    img { transform: scale(1.02); }
  }

  ${(p) => p.$featured && `
    border-color: rgba(168, 85, 247, 0.25);
    box-shadow: ${shadows.card}, 0 0 32px rgba(168,85,247,0.08);
    &:hover {
      border-color: rgba(168, 85, 247, 0.6);
      box-shadow: ${shadows.cardHover}, ${shadows.glowPurple};
    }
  `}
`;

const ImageBox = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${(p) => (p.$featured ? '4/5' : '16/10')};
  border-radius: ${radii.lg};
  overflow: hidden;
  background: ${colors.bgHover};
  margin-bottom: ${space[3]};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${duration.slow} ${ease.soft};
  }
`;

const Meta = styled.div`
  font-family: ${fonts.mono};
  font-size: ${fontSizes.xs};
  color: ${colors.accent};
  margin-bottom: ${space[1]};
  letter-spacing: 0.05em;
`;

const Title = styled.h3`
  font-family: ${fonts.sans};
  font-size: ${(p) => (p.$featured ? fontSizes.lg : fontSizes.md)};
  font-weight: ${fontWeights.bold};
  color: ${colors.text};
  margin-bottom: ${space[1]};
`;

const Tagline = styled.p`
  font-size: ${fontSizes.sm};
  color: ${colors.textDim};
  line-height: 1.4;
  margin-bottom: ${space[2]};
  ${(p) => !p.$featured && `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const Footer = styled.div`
  margin-top: auto;
  padding-top: ${space[3]};
  display: flex;
  justify-content: flex-end;
`;

const VisitLink = styled.a`
  font-family: ${fonts.mono};
  font-size: ${fontSizes.xs};
  color: ${colors.accent};
  display: inline-flex;
  align-items: center;
  gap: ${space[0.5]};
  svg { transition: transform ${duration.base} ${ease.soft}; }
  &:hover svg { transform: translateX(2px); }
`;

const FeaturedTag = styled.span`
  position: absolute;
  top: ${space[3]};
  left: ${space[3]};
  font-family: ${fonts.mono};
  font-size: ${fontSizes.xs};
  color: ${colors.accent};
  letter-spacing: 0.1em;
  z-index: 1;
`;

export function ProjectCard({ project, index, onOpen, $gridArea }) {
  const featured = project.featured;
  const number = String(index + 1).padStart(2, '0');

  const handleClick = (e) => {
    if (e.target.closest('a[data-visit]')) return;
    onOpen(project);
  };
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(project);
    }
  };

  return (
    <Card
      $featured={featured}
      style={$gridArea ? { gridArea: $gridArea } : undefined}
      onClick={handleClick}
      onKeyDown={handleKey}
      tabIndex={0}
      role="button"
      aria-label={`Open details for ${project.title}`}
    >
      {featured && <FeaturedTag aria-hidden="true">FEATURED</FeaturedTag>}
      <ImageBox $featured={featured}>
        <img src={project.image} alt={project.title} loading="lazy" decoding="async" />
      </ImageBox>
      <Meta>{number} / {project.category}</Meta>
      <Title $featured={featured}>{project.title}</Title>
      <Tagline $featured={featured}>{project.tagline}</Tagline>
      <StackTagList>
        {project.stack.map((tag) => <StackTag key={tag}>{tag}</StackTag>)}
      </StackTagList>
      <Footer>
        <VisitLink
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          data-visit
          aria-label={`Visit ${project.title}`}
          onClick={(e) => e.stopPropagation()}
        >
          Visit site <Icon name="arrowRight" size={14} />
        </VisitLink>
      </Footer>
    </Card>
  );
}
